import { DateTime } from "luxon";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

export type AppointmentTab = "today" | "upcoming" | "requests";

interface AppointmentListFilters {
  statusFilter: string;
  workerFilter: string;
  dateFilter: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

interface AppointmentsUIState {
  activeTab: AppointmentTab;
  selectedWorkerIds: string[];
  showAutoSuggestionModal: boolean;
  autoOrganizeTargetDate: Date;
  selectedAppointment: any; // TODO: Add proper type
  showAssignModal: boolean;

  // Multi-service expand/collapse state (global toggle only)
  multiServiceExpanded: boolean; // Global toggle for all multi-service appointments

  // Appointment list filters (persistent)
  filters: AppointmentListFilters;

  setActiveTab: (tab: AppointmentTab | string) => void;
  setSelectedWorkerIds: (workerIds: string[]) => void;
  clearSelectedWorkerIds: () => void;
  toggleWorkerSelection: (workerId: string) => void;
  clearAllFilters: () => void;
  setShowAutoSuggestionModal: (show: boolean) => void;
  setAutoOrganizeTargetDate: (date: Date) => void;
  handleAutoOrganize: (targetDate?: Date) => void;
  setSelectedAppointment: (appointment: any) => void;
  setShowAssignModal: (show: boolean) => void;

  // Multi-service expand/collapse actions
  toggleMultiServiceExpansion: () => void;

  // Appointment list filter actions
  setStatusFilter: (filter: string) => void;
  setWorkerFilter: (filter: string) => void;
  setDateFilter: (filter: string) => void;
  setSortBy: (sortBy: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  clearListFilters: () => void;
}

const defaultFilters: AppointmentListFilters = {
  statusFilter: "all",
  workerFilter: "all",
  dateFilter: "all",
  sortBy: "startTime",
  sortOrder: "asc",
};

export const useAppointmentsUIStore = create<AppointmentsUIState>()(
  devtools(
    persist(
      (set, get) => ({
        activeTab: "today",
        selectedWorkerIds: [],
        showAutoSuggestionModal: false,
        autoOrganizeTargetDate: DateTime.now().toJSDate(),

        // Local appointment page state
        selectedAppointment: null,
        showAssignModal: false,

        // Multi-service expand/collapse state (default collapsed)
        multiServiceExpanded: false,

        // Appointment list filters (persistent)
        filters: defaultFilters,

        setActiveTab: (tab) => {
          set({ activeTab: tab as AppointmentTab });
        },

        setSelectedWorkerIds: (workerIds) => set({ selectedWorkerIds: workerIds }),

        clearSelectedWorkerIds: () => set({ selectedWorkerIds: [] }),

        toggleWorkerSelection: (workerId) =>
          set((state) => ({
            selectedWorkerIds: state.selectedWorkerIds.includes(workerId)
              ? state.selectedWorkerIds.filter((id) => id !== workerId)
              : [...state.selectedWorkerIds, workerId],
          })),

        clearAllFilters: () => set({ selectedWorkerIds: [] }),

        setShowAutoSuggestionModal: (show) => set({ showAutoSuggestionModal: show }),

        setAutoOrganizeTargetDate: (date) => set({ autoOrganizeTargetDate: date }),

        handleAutoOrganize: (targetDate) => {
          const { setAutoOrganizeTargetDate, setShowAutoSuggestionModal } = get();
          setAutoOrganizeTargetDate(targetDate || DateTime.now().toJSDate());
          setShowAutoSuggestionModal(true);
        },

        setSelectedAppointment: (appointment) => set({ selectedAppointment: appointment }),

        setShowAssignModal: (show) => set({ showAssignModal: show }),

        // Multi-service expand/collapse actions
        toggleMultiServiceExpansion: () =>
          set((state) => ({
            multiServiceExpanded: !state.multiServiceExpanded,
          })),

        // Appointment list filter actions
        setStatusFilter: (filter) =>
          set((state) => ({
            filters: { ...state.filters, statusFilter: filter },
          })),

        setWorkerFilter: (filter) =>
          set((state) => ({
            filters: { ...state.filters, workerFilter: filter },
          })),

        setDateFilter: (filter) =>
          set((state) => ({
            filters: { ...state.filters, dateFilter: filter },
          })),

        setSortBy: (sortBy) =>
          set((state) => ({
            filters: { ...state.filters, sortBy },
          })),

        setSortOrder: (order) =>
          set((state) => ({
            filters: { ...state.filters, sortOrder: order },
          })),

        clearListFilters: () =>
          set((state) => ({
            filters: { ...defaultFilters, statusFilter: state.filters.statusFilter },
          })),
      }),
      {
        name: "appointments-ui-store",
        partialize: (state) => ({
          // Persist multi-service expansion preference and list filters
          multiServiceExpanded: state.multiServiceExpanded,
          filters: state.filters,
        }),
      }
    ),
    {
      name: "appointments-ui-store",
    }
  )
);

// Pure function to check if there are active filters
export const hasActiveFilters = (
  selectedWorkerIds: string[],
  filters?: AppointmentListFilters
): boolean => {
  if (selectedWorkerIds.length > 0) return true;

  if (!filters) return false;

  return (
    filters.statusFilter !== defaultFilters.statusFilter ||
    filters.workerFilter !== defaultFilters.workerFilter ||
    filters.dateFilter !== defaultFilters.dateFilter
  );
};

// Convenience hook for appointment list filters
export const useAppointmentListFilters = () => {
  const store = useAppointmentsUIStore();

  return {
    ...store.filters,
    setStatusFilter: store.setStatusFilter,
    setWorkerFilter: store.setWorkerFilter,
    setDateFilter: store.setDateFilter,
    setSortBy: store.setSortBy,
    setSortOrder: store.setSortOrder,
    clearListFilters: store.clearListFilters,
  };
};

// Hook to check if any filters are active (including worker selection and list filters)
export const useHasActiveFilters = () => {
  const { selectedWorkerIds, filters } = useAppointmentsUIStore();
  return hasActiveFilters(selectedWorkerIds, filters);
};
